<script>
import EntityService from "@/services/entityService.js";

export default {
  name: "SolicitacaoDeOrcamentoView",

  //==================================================================//
  //============================= Data ===============================//
  //==================================================================//

  data() {
    return {
      orcamentos: [],
      formularioAberto: false,
      carregando: false,
      formulario: {
        code: "",
        numeroSolicitacao: "",
        dataSolicitacao: new Date().toISOString().split('T')[0],
        cliente: "",
        contato: "",
        email: "",
        telefone: "",
        descricao: "",
        observacoes: "",
        status: "Pendente",
        prazoResposta: "",
        valorEstimado: 0,
        itens: [],
        anexos: []
      },
      statusOpcoes: [
        "Pendente",
        "Em Análise",
        "Orçamento Enviado",
        "Aprovado",
        "Recusado",
        "Cancelado"
      ],
      orcamentoService: new EntityService("SolicitacaoDeOrcamento")
    };
  },

  //==================================================================//
  //======================== Computed ================================//
  //==================================================================//

  computed: {
    //
  },

  //==================================================================//
  //======================== Methods =================================//
  //==================================================================//

  methods: {
    async carregarOrcamentos() {
      try {
        this.carregando = true;
        const response = await this.orcamentoService.getAllEntities();
        this.orcamentos = response.data.data || [];
      } catch (error) {
        console.error("Erro ao carregar orçamentos:", error);
      } finally {
        this.carregando = false;
      }
    },

    abrirFormulario() {
      this.formularioAberto = true;
      this.limparFormulario();
    },

    limparFormulario() {
      this.formulario = {
        code: "",
        numeroSolicitacao: "",
        dataSolicitacao: new Date().toISOString().split('T')[0],
        cliente: "",
        contato: "",
        email: "",
        telefone: "",
        descricao: "",
        observacoes: "",
        status: "Pendente",
        prazoResposta: "",
        valorEstimado: 0,
        itens: [],
        anexos: []
      };
    },

    fecharFormulario() {
      this.formularioAberto = false;
      this.limparFormulario();
    },

    async salvarOrcamento() {
      try {
        this.carregando = true;
        
        if (this.formulario.code) {
          // Atualizar
          await this.orcamentoService.updateEntity(this.formulario);
        } else {
          // Criar
          await this.orcamentoService.createEntity(this.formulario);
        }
        
        this.fecharFormulario();
        await this.carregarOrcamentos();
        alert("Orçamento salvo com sucesso");
      } catch (error) {
        console.error("Erro ao salvar orçamento:", error);
        alert("Erro ao salvar orçamento");
      } finally {
        this.carregando = false;
      }
    },

    editarOrcamento(orcamento) {
      this.formulario = { ...orcamento };
      this.formularioAberto = true;
    },

    async deletarOrcamento(id) {
      if (!confirm("Tem certeza que deseja deletar este orçamento?")) {
        return;
      }

      try {
        this.carregando = true;
        await this.orcamentoService.deleteEntity(id);
        await this.carregarOrcamentos();
        alert("Orçamento deletado com sucesso");
      } catch (error) {
        console.error("Erro ao deletar orçamento:", error);
        alert("Erro ao deletar orçamento");
      } finally {
        this.carregando = false;
      }
    },

    adicionarItem() {
      this.formulario.itens.push({
        descricao: "",
        quantidade: 1,
        unidade: "",
        observacao: ""
      });
    },

    removerItem(index) {
      this.formulario.itens.splice(index, 1);
    }
  },

  //==================================================================//
  //======================== Lifecycle ===============================//
  //==================================================================//

  mounted() {
    this.carregarOrcamentos();
  }
};
</script>

<template>
  <div class="container-fluid mt-4">
    <!-- Cabeçalho -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex justify-content-between align-items-center">
          <h1 class="mb-0">
            <v-icon class="mr-2">mdi-file-document-outline</v-icon>
            Solicitação de Orçamento
          </h1>
          <v-btn
            color="primary"
            @click="abrirFormulario"
            :loading="carregando"
          >
            <v-icon left>mdi-plus</v-icon>
            Nova Solicitação
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Tabela de Orçamentos -->
    <div class="row">
      <div class="col-12">
        <v-card>
          <v-data-table
            :headers="[
              { text: 'ID', value: 'code' },
              { text: 'Número', value: 'numeroSolicitacao' },
              { text: 'Cliente', value: 'cliente' },
              { text: 'Data', value: 'dataSolicitacao' },
              { text: 'Status', value: 'status' },
              { text: 'Valor', value: 'valorEstimado' },
              { text: 'Ações', value: 'actions', sortable: false }
            ]"
            :items="orcamentos"
            :loading="carregando"
            class="elevation-1"
          >
            <template v-slot:item.dataSolicitacao="{ item }">
              {{ new Date(item.dataSolicitacao).toLocaleDateString('pt-BR') }}
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip
                :color="
                  item.status === 'Aprovado' ? 'green' :
                  item.status === 'Recusado' ? 'red' :
                  item.status === 'Orçamento Enviado' ? 'blue' :
                  item.status === 'Cancelado' ? 'gray' :
                  'orange'
                "
                text-color="white"
                small
              >
                {{ item.status }}
              </v-chip>
            </template>

            <template v-slot:item.valorEstimado="{ item }">
              {{ new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.valorEstimado || 0) }}
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn
                icon
                small
                color="primary"
                @click="editarOrcamento(item)"
                class="mr-2"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="red"
                @click="deletarOrcamento(item.code)"
              >
                <v-icon>mdi-trash-can</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </div>
    </div>

    <!-- Modal/Dialog do Formulário -->
    <v-dialog
      v-model="formularioAberto"
      max-width="900px"
      persistent
    >
      <v-card>
        <v-card-title class="bg-blue text-white">
          <v-icon left>mdi-file-document-outline</v-icon>
          {{ formulario.code ? "Editar" : "Nova" }} Solicitação de Orçamento
        </v-card-title>

        <v-card-text class="pt-6">
          <!-- Primeira Seção: Informações Básicas -->
          <v-row class="mb-4">
            <v-col cols="12">
              <h3 class="subtitle-1">Informações da Solicitação</h3>
              <v-divider />
            </v-col>
          </v-row>

          <v-row>
            <!-- ID (Readonly) -->
            <v-col cols="12" md="3">
              <v-text-field
                v-model="formulario.code"
                label="ID"
                readonly
                outlined
              />
            </v-col>

            <!-- Número Solicitação (Readonly) -->
            <v-col cols="12" md="3">
              <v-text-field
                v-model="formulario.numeroSolicitacao"
                label="Número da Solicitação"
                readonly
                outlined
              />
            </v-col>

            <!-- Data Solicitação -->
            <v-col cols="12" md="3">
              <v-text-field
                v-model="formulario.dataSolicitacao"
                type="date"
                label="Data da Solicitação *"
                outlined
                required
              />
            </v-col>

            <!-- Status -->
            <v-col cols="12" md="3">
              <v-select
                v-model="formulario.status"
                :items="statusOpcoes"
                label="Status"
                outlined
              />
            </v-col>
          </v-row>

          <!-- Segunda Seção: Dados do Cliente -->
          <v-row class="mt-4 mb-4">
            <v-col cols="12">
              <h3 class="subtitle-1">Dados do Cliente</h3>
              <v-divider />
            </v-col>
          </v-row>

          <v-row>
            <!-- Cliente -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formulario.cliente"
                label="Cliente *"
                outlined
                required
              />
            </v-col>

            <!-- Contato -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formulario.contato"
                label="Contato"
                outlined
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Email -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formulario.email"
                label="E-mail"
                type="email"
                outlined
              />
            </v-col>

            <!-- Telefone -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formulario.telefone"
                label="Telefone"
                outlined
              />
            </v-col>
          </v-row>

          <!-- Terceira Seção: Detalhes da Solicitação -->
          <v-row class="mt-4 mb-4">
            <v-col cols="12">
              <h3 class="subtitle-1">Detalhes da Solicitação</h3>
              <v-divider />
            </v-col>
          </v-row>

          <v-row>
            <!-- Descrição -->
            <v-col cols="12">
              <v-textarea
                v-model="formulario.descricao"
                label="Descrição da Solicitação *"
                outlined
                rows="3"
                required
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Observações -->
            <v-col cols="12">
              <v-textarea
                v-model="formulario.observacoes"
                label="Observações"
                outlined
                rows="2"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Prazo -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formulario.prazoResposta"
                type="date"
                label="Prazo para Resposta"
                outlined
              />
            </v-col>

            <!-- Valor Estimado -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="formulario.valorEstimado"
                label="Valor Estimado (R$)"
                type="number"
                outlined
                step="0.01"
              />
            </v-col>
          </v-row>

          <!-- Quarta Seção: Itens -->
          <v-row class="mt-4 mb-4">
            <v-col cols="12">
              <div class="d-flex justify-content-between align-items-center">
                <h3 class="subtitle-1 mb-0">Itens Solicitados</h3>
                <v-btn small color="primary" @click="adicionarItem">
                  <v-icon left small>mdi-plus</v-icon>
                  Adicionar Item
                </v-btn>
              </div>
              <v-divider />
            </v-col>
          </v-row>

          <v-row v-if="formulario.itens.length === 0">
            <v-col cols="12">
              <p class="text-center text-muted">Nenhum item adicionado</p>
            </v-col>
          </v-row>

          <div v-for="(item, index) in formulario.itens" :key="index" class="mb-4">
            <v-card outlined class="pa-4">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="item.descricao"
                    label="Descrição do Item"
                    outlined
                    small
                  />
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model.number="item.quantidade"
                    label="Quantidade"
                    type="number"
                    outlined
                    small
                  />
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="item.unidade"
                    label="Unidade"
                    outlined
                    small
                  />
                </v-col>
                <v-col cols="12" md="2" class="d-flex align-center">
                  <v-btn small icon color="red" @click="removerItem(index)">
                    <v-icon>mdi-trash-can</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="item.observacao"
                    label="Observação do Item"
                    outlined
                    rows="2"
                    small
                  />
                </v-col>
              </v-row>
            </v-card>
          </div>
        </v-card-text>

        <v-card-actions class="pa-6 bg-light">
          <v-spacer />
          <v-btn
            text
            @click="fecharFormulario"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            @click="salvarOrcamento"
            :loading="carregando"
          >
            <v-icon left>mdi-content-save</v-icon>
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.bg-blue {
  background-color: #1976d2;
}

.bg-light {
  background-color: #f5f5f5;
}

.container-fluid {
  max-width: 1400px;
  margin: 0 auto;
}

.text-muted {
  color: #999;
}

.subtitle-1 {
  font-weight: 600;
  color: #333;
}
</style>
